import React from 'react';

import { isValue } from '../../utility/isValue';
import { renderMoneyAmountShort } from '../../components/formBuilder/utility/renderValue';
import OverviewDivider from '../../components/property-summaries/OverviewDivider';

export default function PurchaseSummaryOverview({ propertyData }) {
  const downPayment = propertyData.downPaymentAmount;
  const closingCosts = propertyData.totalCashRequiredToClose;
  const totalCosts = propertyData.downPaymentAmount + closingCosts;
  const monthlyGOI = propertyData?.monthlyGOI;
  const monthlyDebtService = propertyData?.monthlyDebtService || 0;
  const cashFlow = propertyData.annualPreTaxCashFlow / 12;
  const totalMonthlyOperatingExpensesPurchase =
    propertyData?.totalMonthlyOperatingExpensesPurchase;
  const netIncome = calculateNetIncome(
    monthlyGOI,
    totalMonthlyOperatingExpensesPurchase
  );

  return (
    <section className="rounded-lg relative bg-white p-4">
      <div className="flex flex-col sm:flex-row relative sm:flex-nowrap items-stretch gap-2 sm:gap-0">
        <div className="flex-grow bg-[#F5F6F4] rounded-lg sm:rounded-r-none sm:rounded-l-lg py-4 px-3 lg:px-6">
          <div className="relative">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-neutral-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[#F5F6F4] px-5 text-md text-neutral-600">
                Purchase Cost
              </span>
            </div>
          </div>
          <div className="px-6 lg:px-12 mt-7 mb-4">
            <div className="grid grid-cols-3 gap-2">
              <div className="flex flex-col justify-center">
                <h4 className="text-[12px] font-normal text-neutral-600">
                  Down Payment
                </h4>
                <p className="text-xl font-serif text-neutral-900 font-medium">
                  {renderMoneyAmountShort(downPayment)}
                </p>
              </div>
              <div className="flex flex-col justify-center">
                <h4 className="text-[12px] font-normal text-neutral-600">
                  Closing Costs
                </h4>
                <p className="text-xl font-serif text-neutral-900 font-medium">
                  {renderMoneyAmountShort(closingCosts)}
                </p>
              </div>
              <div className="flex flex-col justify-center">
                <h4 className="font-normal text-neutral-600 text-md">
                  Total Costs
                </h4>
                <p className="text-6xl font-serif text-neutral-900 font-medium printing-text-spacing">
                  {renderMoneyAmountShort(totalCosts)}
                </p>
              </div>
            </div>
          </div>
        </div>
        <OverviewDivider />
        <div className="flex-grow bg-[#EFF8F3] rounded-lg sm:rounded-l-none rounded-r-lg py-4 px-3 lg:px-6">
          <div className="relative">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-neutral-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[#EFF8F3] text-md px-5 text-neutral-600">
                Cashflow on purchase
              </span>
            </div>
          </div>
          <div className="px-6 lg:px-12 mt-7 mb-4">
            <div className="grid grid-cols-3 gap-2">
              <div className="flex flex-col justify-center">
                <h4 className="text-base font-normal text-neutral-600">
                  Net Income
                </h4>
                <p className="text-xl font-serif text-neutral-900 font-medium">
                  {renderMoneyAmountShort(netIncome)}
                  <span className="text-sm">/mo</span>
                </p>
              </div>
              <div className="flex flex-col justify-center">
                <h4 className="text-base font-normal text-neutral-600">
                  Payment
                </h4>
                <p className="text-xl font-serif text-neutral-900 font-medium">
                  {renderMoneyAmountShort(monthlyDebtService)}
                  <span className="text-sm">/mo</span>
                </p>
              </div>
              <div className="flex flex-col justify-center">
                <h4 className="font-normal text-neutral-600 text-md">
                  Cashflow
                </h4>
                <p className="text-6xl font-serif text-neutral-900 font-medium printing-text-spacing">
                  {renderMoneyAmountShort(cashFlow)}
                  <span className="text-3xl">/mo</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function calculateNetIncome(monthlyGOI, totalMonthlyOperatingExpensesPurchase) {
  if (isValue(monthlyGOI) && isValue(totalMonthlyOperatingExpensesPurchase)) {
    return (
      Math.round(
        monthlyGOI * 100 - totalMonthlyOperatingExpensesPurchase * 100
      ) / 100
    );
  }

  return null;
}
